/**
 * ProjectDetail - a project as a canvas with panes floating over its edges.
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page used to be a `Header` + `FilterBarWithTabs` + a fixed two-column
 * grid, whose right `lg:col-span-2` column carried the linked-resource
 * summary and the collaborator list. That column only existed on the Details
 * tab, so the moment someone opened Resources or the Feed the answer to "who
 * is on this and what is attached" left the screen. The audit log was worse:
 * a hand-rolled `fixed inset-y-0 end-0 w-96` drawer with its own black
 * scrim, living outside every layout the page had.
 *
 * All of it is now PANES on the shared `DetailPageSurface` - so they dock
 * left or right, stack, reorder by dragging their header, resize, and are
 * remembered per user per project. See `surface/layout`.
 *
 * THE LAYERING, outside in:
 *
 *   DetailFrame        breadcrumbs, the primary action, the overflow menu
 *     DetailTabBar     the canvas's tabs + the pane rail, full width
 *     DetailPageSurface  the canvas, with the panes over its edges
 *
 * The tab bar is a SIBLING of the surface, not its first child: navigation is
 * the frame around the work, so a docked pane must not sit over it or shrink
 * it.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  fetchProjectDetail,
  updateProject,
  deleteProject,
  clearProjectDetail,
  fetchProjectResources,
  fetchProjectCollaborators,
  fetchProjectDashboard,
  PROJECT_DETAIL_FORBIDDEN,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { buildProjectCollaboratorAdapter } from '../../utils/collaboratorAdapters/project';
import DeleteProjectConfirmation from './DeleteProjectConfirmation';
import DetailFrame from '../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import PaneBody from '../shared/surface/PaneBody';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import DateTimePicker from '../shared/TailwindComponents/DatePicker';
import Textarea from '../shared/TailwindComponents/Textarea';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import GlassCard from '../shared/brand/GlassCard';
import { Field, FactTile, FieldGrid } from '../shared/brand/Field';
import { formatDateTime, parseServerDate } from '../../utils/functions';
import {
  dueCountdown,
  dueTone as dueToneFor,
  priorityColorMap,
  statusColorMap,
  titleCase,
} from './projectFacts';
import {
  PiPencilSimpleLight,
  PiTrashLight,
  PiUsersThreeLight,
  PiLinkSimpleLight,
  PiClockCounterClockwiseLight,
  PiCalendarBlankLight,
  PiFlagLight,
  PiFlagPennantLight,
  PiCircleDashedLight,
  PiTextAlignLeftLight,
  PiPlusLight,
  PiInfoLight,
  PiClockCountdownLight,
  PiLinkSimpleHorizontalLight,
  PiProjectorScreenChartLight,
  PiCoinsLight,
} from 'react-icons/pi';
import ProjectResourcesTab from './ProjectResourcesTab';
import ProjectFeed from './ProjectFeed';
import ProjectMilestones from './ProjectMilestones';
import { FeatureTour, projectDetailOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { PROJECT_SURFACE, PROJECT_TABS, useProjectWorkspace } from './surface';
import { PROJECT_ECONOMICS_ENABLED, PROJECT_MILESTONES_ENABLED } from './surface/layout';
import { ProjectSurfaceProvider } from './surface/ProjectSurfaceContext';
import ProjectContextPane from './surface/ProjectContextPane';
import ProjectLinkedResourcesPane from './surface/ProjectLinkedResourcesPane';
import ProjectEconomicsPane from './surface/ProjectEconomicsPane';
import AccessPane from '../shared/access/AccessPane';

// ─── Config ─────────────────────────────────────────────────────

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'completed', label: 'Completed' },
];
const priorityOptions = [
  { value: '', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// ─── Component ──────────────────────────────────────────────────

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { currentProject, isLoadingDetail, resources } = useSelector((state: RootState) => state.projects);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /* The Milestones pane's add state - the button lives in the pane header,
     next to the close ×, so the page holds the flag the list reads. */
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  const [editData, setEditData] = useState({ title: '', description: '', status: 'active', priority: '', due_date: '' });

  /**
   * The workspace: pane layout, the reader's content and the active tab, from
   * the ONE shared hook. Keyed by project id AND by user, so each project
   * remembers its own arrangement and nobody inherits a colleague's - see
   * `surface/layout` and `services/layout/layoutStore`.
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
  } = useProjectWorkspace(projectId, {
    // The raw query param: the hook checks it against the surface's declared
    // tabs and falls back to the first one, so a stale `?tab=` from an older
    // link cannot leave the canvas blank.
    initialTab: new URLSearchParams(location.search).get('tab') || undefined,
    onTabChange: (tab) => history.replace(`/projects/${projectId}?tab=${tab}`),
  });

  const {
    isRunning: isProjectDetailTourRunning,
    handleJoyrideCallback: handleProjectDetailTourCallback,
  } = useFeatureTour({
    tourId: `project-detail-orientation-${projectId}`,
    steps: projectDetailOrientationSteps,
    readyToShow: !!currentProject,
    autoStartDelay: 700,
  });

  useEffect(() => {
    let cancelled = false;
    if (projectId) {
      // Fetch detail first; if the user has no access (403/404), redirect to
      // the projects list before kicking off the sub-resource fetches that
      // would otherwise produce noisy 403 toasts.
      (async () => {
        const result = await dispatch(fetchProjectDetail(projectId));
        if (cancelled) return;
        if (result === PROJECT_DETAIL_FORBIDDEN) {
          history.replace('/projects');
          return;
        }
        if (!result) return;
        dispatch(fetchProjectResources(projectId));
        dispatch(fetchProjectCollaborators(projectId));
        dispatch(fetchProjectDashboard(projectId));
      })();
    }
    return () => { cancelled = true; dispatch(clearProjectDetail()); };
  }, [dispatch, projectId, history]);

  useEffect(() => {
    if (currentProject) {
      setEditData({
        title: currentProject.title || '',
        description: currentProject.description || '',
        status: currentProject.status || 'active',
        priority: currentProject.priority || '',
        due_date: currentProject.due_date || '',
      });
    }
  }, [currentProject]);

  const myAccessLevel: string = (currentProject as any)?.my_access_level || 'view';
  const canEdit = myAccessLevel === 'edit' || myAccessLevel === 'admin';
  const canAdmin = myAccessLevel === 'admin';

  const handleSaveEdit = async () => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      const result = await dispatch(updateProject(projectId, editData));
      if (result) {
        setIsEditing(false);
        dispatch(fetchProjectDetail(projectId));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Delete Project'),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectName={currentProject?.title}
          onConfirm={async () => {
            await dispatch(deleteProject(projectId!));
            dispatch(hideModal());
            history.push('/projects');
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleManageCollaborators = useCallback(() => {
    const adapter = buildProjectCollaboratorAdapter(dispatch, {
      projectUuid: projectId!,
      collaborators: currentProject?.shared_with || [],
      currentUserUuid: currentProject?.owner?.user_id ? String(currentProject.owner.user_id) : undefined,
      canAdmin,
      onUpdate: () => {
        dispatch(fetchProjectDetail(projectId!));
        dispatch(fetchProjectCollaborators(projectId!));
      },
    });
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: canAdmin ? t('Manage Access') : t('Access'),
      icon: <PiUsersThreeLight className="w-5 h-5" />,
      onCancel: () => dispatch(hideSidePanel()),
      ...adapter,
    } as any));
  }, [dispatch, projectId, currentProject, canAdmin, t]);

  const handleOpenResourcePanel = useCallback(() => {
    dispatch(showSidePanel({
      type: 'projectResources', showSidePanel: true, size: 'lg',
      projectUuid: projectId!,
      message: t('Link Resources'),
      subMsg: t('Search and link resources to this project.'),
      icon: <PiLinkSimpleHorizontalLight className="w-5 h-5 text-fg-muted" />,
      onResourceLinked: () => { dispatch(fetchProjectResources(projectId!)); dispatch(fetchProjectDetail(projectId!)); },
    } as any));
  }, [dispatch, projectId, t]);

  const handleEditPricing = useCallback(() => {
    dispatch(showSidePanel({
      type: 'projectPricing',
      showSidePanel: true,
      size: 'md',
      projectUuid: projectId!,
      project: currentProject,
      message: t('Pricing'),
      subMsg: t('Set how this project is billed.'),
      icon: <PiCoinsLight className="w-5 h-5 text-fg-muted" />,
      onSaved: () => { dispatch(fetchProjectDetail(projectId!)); },
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  }, [dispatch, projectId, currentProject, t]);

  /* The VALUES come from the surface config - one vocabulary for the bar,
     the URL and `goToTab`. Only the labels are the page's. */
  const TAB_LABELS: Record<typeof PROJECT_TABS[number], string> = {
    Details: t('Details'),
    Resources: t('Resources'),
    Feed: t('Feed'),
  };
  const tabs: TabItem[] = PROJECT_TABS.map((value) => ({ label: TAB_LABELS[value], value }));

  const breadcrumbs = [
    { name: t('Projects'), href: '/projects', current: false },
    { name: currentProject?.title || t('Untitled'), href: '#', current: true },
  ];

  // ─── Loading ──────────────────────────────────────────────────

  if (isLoadingDetail && !currentProject) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" />
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-fg-muted">
        <PiProjectorScreenChartLight className="w-10 h-10 text-fg-muted mb-3" />
        <p className="text-sm">{t('Project not found')}</p>
        <Button variant="outline" size="small" onClick={() => history.push('/projects')} className="mt-3">{t('Back to Projects')}</Button>
      </div>
    );
  }

  const dropdownActions = [
    ...(canEdit ? [{ label: t('Edit'), onClick: () => { goToTab('Details'); setIsEditing(true); }, icon: <PiPencilSimpleLight className="w-4 h-4" /> }] : []),
    /* NO "Manage Access" HERE - the rail's Access pill (badged with the
       count) raises the pane that lists who is on this project, and the
       pane's own button opens the editor. */
    // The activity log is a PANE now, not a drawer: the menu item raises it.
    { label: t('Activity Log'), onClick: () => surfaceApi.openPane('activity'), icon: <PiClockCounterClockwiseLight className="w-4 h-4" /> },
    ...(canAdmin ? [{ label: t('Delete'), onClick: handleDelete, icon: <PiTrashLight className="w-4 h-4" />, variant: 'destructive' as const }] : []),
  ];

  const countdown = dueCountdown(currentProject.due_date);
  const dueTone = dueToneFor(currentProject.due_date);

  return (
    <ProjectSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={canEdit ? [{ label: t('Link Resource'), onClick: handleOpenResourcePanel, icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' as const, tourId: 'project-link-resource' }] : []}
      dropdownActions={dropdownActions}
      headerTourId="project-header"
      dropdownTourId="project-actions-menu"
      showBackButton
    >
      <div className="flex-1 flex flex-col min-h-0">
        {/*
          THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE THE SURFACE - so a
          docked pane floats below them rather than over them, and the row
          does not shrink as panes open.
        */}
        <div data-tour-id="project-tabs" className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={goToTab}
            tourId="project-tabs-left"
            /* Tabs left, pane toggles right, one row. `labels="auto"` drops
               the rail to icons below xl so a laptop shares the row instead
               of clipping it. */
            actions={(
              <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                /* Straight from the surface config: every pane that asked for
                   a rail toggle gets one, in declaration order, so a new pane
                   cannot be added and then forgotten here. */
                items={PROJECT_SURFACE.railItems({
                  t,
                  icons: {
                    context: <PiInfoLight />,
                    resources: <PiLinkSimpleLight />,
                    access: <PiUsersThreeLight />,
                    economics: <PiCoinsLight />,
                    milestones: <PiFlagPennantLight />,
                    activity: <PiClockCounterClockwiseLight />,
                  },
                  // The record's own totals, so a pill is right whether or not
                  // its pane has ever been opened.
                  badges: {
                    resources: currentProject.total_resources || 0,
                    access: currentProject.shared_with?.length || 0,
                  },
                })}
              />
            )}
          />
        </div>

        <DetailPageSurface
          surface={PROJECT_SURFACE}
          layoutApi={panels}
          panes={[
            {
              id: 'context',
              title: t('Context'),
              icon: <PiInfoLight />,
              body: (
                <ProjectContextPane
                  project={currentProject}
                  onOpenAccess={() => surfaceApi.openPane('access')}
                  onOpenResources={() => goToTab('Resources')}
                />
              ),
            },
            {
              id: 'resources',
              title: t('Resources'),
              icon: <PiLinkSimpleLight />,
              body: (
                <ProjectLinkedResourcesPane
                  project={currentProject}
                  resources={resources}
                  canEdit={canEdit}
                  onLink={handleOpenResourcePanel}
                  onOpenResources={() => goToTab('Resources')}
                />
              ),
            },
            {
              id: 'access',
              title: t('Access'),
              icon: <PiUsersThreeLight />,
              body: (
                <AccessPane
                  collaborators={currentProject.shared_with || []}
                  onManageAccess={handleManageCollaborators}
                  canManage={canAdmin}
                />
              ),
            },
            /*
              ECONOMICS IS SWITCHED OFF - `PROJECT_ECONOMICS_ENABLED` in
              `surface/layout` (proj-7 deferred). Same arrangement as
              milestones: the pane stays declared here, so re-enabling is the
              one constant and nothing else; with the flag off the id is not in
              the surface, so there is no pane, no rail pill and no route to
              the pricing editor.
            */
            ...(PROJECT_ECONOMICS_ENABLED ? [{
              id: 'economics' as const,
              title: t('Economics'),
              icon: <PiCoinsLight />,
              body: (
                <ProjectEconomicsPane
                  project={currentProject}
                  canEdit={canEdit}
                  onEditPricing={handleEditPricing}
                />
              ),
            }] : []),
            /*
              MILESTONES ARE SWITCHED OFF - `PROJECT_MILESTONES_ENABLED` in
              `surface/layout`. The pane stays declared here so re-enabling is
              the one constant and nothing else; with the flag off the id is
              not in the surface, so the shell never looks for this entry.

              Its ADD BUTTON is the pane's, not the list's: it sits in the
              pane header next to the close ×, which is why `isAdding` is
              held here rather than inside `ProjectMilestones`.
            */
            ...(PROJECT_MILESTONES_ENABLED ? [{
              id: 'milestones' as const,
              title: t('Milestones'),
              icon: <PiFlagPennantLight />,
              actions: (
                <Button variant="neutral" size="small" onClick={() => setIsAddingMilestone(true)}>
                  <PiPlusLight className="h-3.5 w-3.5 me-1" />{t('Add')}
                </Button>
              ),
              /* Keyed by project: the list is fetched per project, and moving
                 between them must not show the previous one's phases. */
              body: (
                <ProjectMilestones
                  key={projectId}
                  projectUuid={projectId!}
                  isAdding={isAddingMilestone}
                  onIsAddingChange={setIsAddingMilestone}
                />
              ),
            }] : []),
            {
              id: 'activity',
              title: t('Activity'),
              icon: <PiClockCounterClockwiseLight />,
              /* A thunk: the log fetches on mount, so it is built only while
                 the pane is actually on screen. No `hideHeader` either - it
                 reads `useInPane()` and renders its body alone. */
              body: () => (
                <PaneBody>
                  <AuditLogPanel
                    entityType="project"
                    entityId={projectId!}
                    entityName={currentProject.title || ''}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                </PaneBody>
              ),
            },
          ]}
        >
          {activeTab === 'Details' && (
            <div className="details-canvas flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
              <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
              <GlassCard
                icon={<PiTextAlignLeftLight />}
                title={t('Description')}
                padding="lg"
                actions={
                  !isEditing && canEdit ? (
                    <Button variant="outline" size="small" onClick={() => setIsEditing(true)}>
                      <PiPencilSimpleLight className="h-3.5 w-3.5 me-1" />{t('Edit')}
                    </Button>
                  ) : undefined
                }
              >
                {isEditing ? (
                  <div className="space-y-4">
                    <Input id="project-title" type="text" label={t('Title')} value={editData.title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditData({ ...editData, title: e.target.value })} />
                    <Textarea id="project-description" label={t('Description')} value={editData.description} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditData({ ...editData, description: e.target.value })} rows={4} />
                    <div className="details-grid-3 gap-3">
                      <Dropdown label={t('Status')} options={statusOptions} value={editData.status} onChange={(v) => setEditData({ ...editData, status: Array.isArray(v) ? v[0] : v })} />
                      <Dropdown label={t('Priority')} options={priorityOptions} value={editData.priority} onChange={(v) => setEditData({ ...editData, priority: Array.isArray(v) ? v[0] : v })} />
                      <DateTimePicker
                        id="project-due-date"
                        label={t('Due Date')}
                        selected={parseServerDate(editData.due_date)}
                        onChange={(date) => setEditData({ ...editData, due_date: date ? date.toISOString() : '' })}
                        showTimeSelect={false}
                        fullWidth
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button variant="primary" size="small" onClick={handleSaveEdit} disabled={isSaving}>{isSaving ? t('Saving...') : t('Save')}</Button>
                      <Button variant="neutral" size="small" onClick={() => setIsEditing(false)}>{t('Cancel')}</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-fg-body whitespace-pre-wrap leading-relaxed max-w-prose">
                    {currentProject.description || <span className="text-fg-muted">{t('No description yet. Click edit to add one.')}</span>}
                  </p>
                )}
              </GlassCard>

              <FieldGrid measure="container" columns={4}>
                <FactTile icon={<PiCircleDashedLight />} label={t('Status')}>
                  <Badge color={statusColorMap[currentProject.status] || 'gray'}>
                    {currentProject.status ? titleCase(currentProject.status) : t('Active')}
                  </Badge>
                </FactTile>
                <FactTile icon={<PiFlagLight />} label={t('Priority')}>
                  {currentProject.priority ? (
                    <Badge color={priorityColorMap[currentProject.priority] || 'gray'}>
                      {titleCase(currentProject.priority)}
                    </Badge>
                  ) : null}
                </FactTile>
                <FactTile icon={<PiCalendarBlankLight />} label={t('Created')} value={formatDateTime(currentProject.created_at)} emptyText="-" />
                <GlassCard tone={dueTone}>
                  <Field icon={<PiClockCountdownLight />} label={t('Due Date')} tone={dueTone}>
                    {currentProject.due_date ? (
                      <div className="flex flex-col gap-0.5">
                        <p className={clsx('text-sm font-medium', dueTone === 'danger' ? 'text-danger-600 dark:text-danger-400' : dueTone === 'warning' ? 'text-warning-600 dark:text-warning-400' : 'text-fg-body')}>
                          {formatDateTime(currentProject.due_date)}
                        </p>
                        {countdown && (
                          <span className={clsx('text-[11px] font-medium', dueTone === 'danger' ? 'text-danger-500 dark:text-danger-400' : 'text-warning-600 dark:text-warning-400')}>
                            {countdown}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </Field>
                </GlassCard>
              </FieldGrid>
              </div>
            </div>
          )}

          {activeTab === 'Resources' && (
            <ProjectResourcesTab projectUuid={projectId!} onOpenLinkPanel={handleOpenResourcePanel} />
          )}

          {activeTab === 'Feed' && <ProjectFeed projectUuid={projectId!} />}
        </DetailPageSurface>
      </div>

      <FeatureTour
        steps={projectDetailOrientationSteps}
        run={isProjectDetailTourRunning}
        onCallback={handleProjectDetailTourCallback}
        continuous
      />
    </DetailFrame>
    </ProjectSurfaceProvider>
  );
};

export default ProjectDetail;
